// Saisie d'un équipement. Une case vide = valeur inconnue (affichée « Inconnu »), jamais devinée.
// « Lire la plaque » : l'assistance lit une photo de la plaque signalétique ; les champs VIDES sont pré-remplis et marqués
// « Lu sur la photo — à confirmer » ; une valeur déjà saisie n'est jamais écrasée (l'écart est montré). Rien n'est enregistré
// sans la confirmation explicite du technicien.
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ScanText } from 'lucide-react';
import { api, ErreurApi, messageErreur } from '../../lib/api.ts';
import { formulairePhoto, reduirePhoto } from '../../lib/photo.ts';
import { Alerte, Bouton, Champ, ChampChoix, ChampTexte, Dialogue, LU_SUR_PHOTO, MarqueIa, useNotifier } from '../../components/ui.tsx';
import { BoutonPhoto, useLecturePhoto, type Plaque } from '../../components/LecturePhoto.tsx';
import { PROTOCOLES } from '../../components/statuts.tsx';

export interface Lieu { id: string; parent_id: string | null; kind: 'batiment' | 'etage' | 'zone'; name: string; level: number | null;
  floor_height_m?: string | null; floor_height_source?: string | null }
export interface Systeme { id: string; name: string; domain: string; protocols: string[] }
export interface EquipementSaisie {
  id?: string; label: string; category: string | null; manufacturer: string | null; range_name: string | null; reference: string | null;
  hw_version: string | null; sw_version: string | null; firmware: string | null; serial_number?: string | null; protocols: string[];
  network_address: string | null; location_id: string | null; system_id: string | null; installed_on?: string | null; notes?: string | null;
}

export const CATEGORIES = ['Automate', 'Contrôleur', 'Passerelle', 'Superviseur', 'Module E/S', 'Alimentation bus', 'Capteur', 'Actionneur',
  'Variateur', 'Compteur', 'Thermostat', 'Écran / poste de commande', 'Commutateur réseau', 'Autre'];

export function cheminLieu(lieux: Lieu[], id: string | null) {
  const parties: string[] = [];
  let l = lieux.find((x) => x.id === id);
  while (l) { parties.unshift(l.name); l = lieux.find((x) => x.id === l!.parent_id); }
  return parties.join(' › ');
}

// ─── Lecture de la plaque : correspondance champs ↔ valeurs lues ───
export type ChampLu = 'manufacturer' | 'range_name' | 'reference' | 'hw_version' | 'sw_version' | 'firmware' | 'serial_number';
export const LIBELLES_LUS: Record<ChampLu, string> = {
  manufacturer: 'Fabricant', range_name: 'Gamme', reference: 'Référence exacte', hw_version: 'Version matérielle',
  sw_version: 'Version logicielle', firmware: 'Firmware', serial_number: 'N° de série',
};
const valeursLues = (p: Plaque): [ChampLu, string | null][] => [
  ['manufacturer', p.fabricant], ['range_name', p.gamme], ['reference', p.reference], ['hw_version', p.versions.materiel],
  ['sw_version', p.versions.logiciel], ['firmware', p.versions.firmware], ['serial_number', p.numero_serie],
];

/** Pré-remplit les champs vides (marqués « lus ») ; un champ déjà saisi et différent devient un écart montré, jamais écrasé. */
export function appliquerPlaque(f: EquipementSaisie, p: Plaque) {
  const suivant = { ...f };
  const lus: Partial<Record<ChampLu, string>> = {};
  const ecarts: { champ: ChampLu; valeur: string }[] = [];
  for (const [champ, valeur] of valeursLues(p)) {
    if (!valeur) continue;
    const actuel = (f[champ] ?? '').trim();
    if (!actuel) { (suivant as any)[champ] = valeur; lus[champ] = valeur; }
    else if (actuel !== valeur) ecarts.push({ champ, valeur });
  }
  return { f: suivant, lus, ecarts };
}

export function FormulaireEquipement({ ouvert, fermer, siteId, lieux, systemes, initial, enregistre, proposition, photoAjoutee }: {
  ouvert: boolean; fermer: () => void; siteId: string; lieux: Lieu[]; systemes: Systeme[]; initial?: EquipementSaisie; enregistre: (id: string) => void;
  /** Lecture déjà faite depuis la fiche (photo enregistrée) : appliquée à l'ouverture. */
  proposition?: Plaque | null;
  /** Une photo a été ajoutée à la fiche pendant la saisie (pour rafraîchir la fiche). */
  photoAjoutee?: () => void;
}) {
  const vide: EquipementSaisie = { label: '', category: null, manufacturer: null, range_name: null, reference: null, hw_version: null,
    sw_version: null, firmware: null, serial_number: null, protocols: [], network_address: null, location_id: null, system_id: null, installed_on: null, notes: null };
  const [f, setF] = useState<EquipementSaisie>(initial ?? vide);
  const courant = useRef(f);
  courant.current = f;
  const [erreur, setErreur] = useState<string | null>(null);
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const [envoi, setEnvoi] = useState(false);
  // Lecture de la plaque
  const lp = useLecturePhoto();
  const notifier = useNotifier();
  const [lus, setLus] = useState<Partial<Record<ChampLu, string>>>({});
  const [notesLues, setNotesLues] = useState(false);
  const [ecarts, setEcarts] = useState<{ champ: ChampLu; valeur: string }[]>([]);
  const [infos, setInfos] = useState<Pick<Plaque, 'autres' | 'illisible'> & { vide: boolean } | null>(null);
  const [confirme, setConfirme] = useState(false);
  const [photoEnAttente, setPhotoEnAttente] = useState<Blob | null>(null);
  const [lecture, setLecture] = useState(false);
  const [erreurLecture, setErreurLecture] = useState<string | null>(null);
  const caseConfirmation = useRef<HTMLInputElement>(null);

  const fabricants = useQuery({ queryKey: ['fabricants'], queryFn: () => api.get<{ nom: string }[]>('/api/referentiel/fabricants'), enabled: ouvert });
  const v = (k: keyof EquipementSaisie) => (f[k] as string | null) ?? '';
  const maj = (k: keyof EquipementSaisie) => (e: { target: { value: string } }) => {
    setF({ ...f, [k]: e.target.value === '' ? null : e.target.value });
    // Une valeur corrigée par le technicien n'est plus « lue sur la photo ».
    if (k in lus) setLus((l) => { const { [k as ChampLu]: _retire, ...reste } = l; return reste; });
    if (k === 'notes') setNotesLues(false);
  };

  const recevoir = (p: Plaque) => {
    const r = appliquerPlaque(courant.current, p);
    setF(r.f);
    setLus((l) => ({ ...l, ...r.lus }));
    setEcarts(r.ecarts);
    setInfos({ autres: p.autres, illisible: p.illisible, vide: !valeursLues(p).some(([, x]) => x) && !p.autres.length });
    setConfirme(false);
  };
  useEffect(() => { if (proposition && ouvert) recevoir(proposition); }, [proposition, ouvert]);

  const lirePhoto = async (fichier: File) => {
    setLecture(true); setErreurLecture(null);
    try {
      const photo = await reduirePhoto(fichier);
      let plaque: Plaque;
      if (initial?.id) {
        // Équipement existant : la photo rejoint la fiche, puis elle est lue.
        const { id } = await api.envoyer<{ id: string }>(`/api/equipements/${initial.id}/photos`, formulairePhoto(photo));
        photoAjoutee?.();
        ({ plaque } = await api.post<{ plaque: Plaque }>(`/api/equipements/${initial.id}/photos/${id}/lire-plaque`));
      } else {
        // Nouvel équipement : lue sans être conservée ; jointe à la fiche à l'enregistrement.
        ({ plaque } = await api.envoyer<{ plaque: Plaque }>(`/api/sites/${siteId}/lire-plaque`, formulairePhoto(photo)));
        setPhotoEnAttente(photo);
      }
      recevoir(plaque);
    } catch (e) {
      setErreurLecture(e instanceof ErreurApi ? e.message : e instanceof Error ? e.message : messageErreur(e));
    } finally { setLecture(false); }
  };

  const utiliserLue = (champ: ChampLu, valeur: string) => {
    setF({ ...f, [champ]: valeur });
    setLus((l) => ({ ...l, [champ]: valeur }));
    setEcarts((x) => x.filter((y) => y.champ !== champ));
    setConfirme(false);
  };
  const ajouterAuxNotes = () => {
    if (!infos?.autres.length) return;
    const lignes = infos.autres.map((a) => `${a.libelle} : ${a.valeur}`).join('\n');
    setF({ ...f, notes: `${f.notes ? `${f.notes}\n` : ''}Relevé sur la plaque :\n${lignes}` });
    setNotesLues(true); setConfirme(false);
  };

  const aConfirmer = Object.keys(lus).length > 0 || notesLues;

  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    if (!f.label.trim()) { setErreurs({ label: 'Indiquez une désignation (ex. « Automate CTA 2 »).' }); return; }
    if (aConfirmer && !confirme) {
      setErreurs({ confirmation: 'Comparez les valeurs marquées « Lu sur la photo » avec la plaque, puis cochez la confirmation.' });
      caseConfirmation.current?.focus();
      return;
    }
    setEnvoi(true); setErreur(null); setErreurs({});
    const corps = {
      label: f.label, category: f.category, manufacturer: f.manufacturer, rangeName: f.range_name, reference: f.reference,
      hwVersion: f.hw_version, swVersion: f.sw_version, firmware: f.firmware, serialNumber: f.serial_number ?? null, protocols: f.protocols,
      networkAddress: f.network_address, locationId: f.location_id, systemId: f.system_id, installedOn: f.installed_on || null, notes: f.notes ?? null,
    };
    try {
      const r = initial?.id
        ? await api.patch<{ id: string }>(`/api/equipements/${initial.id}`, corps)
        : await api.post<{ id: string }>(`/api/sites/${siteId}/equipements`, corps);
      if (photoEnAttente && !initial?.id) {
        try { await api.envoyer(`/api/equipements/${r.id}/photos`, formulairePhoto(photoEnAttente)); }
        catch (err) { notifier(`Équipement enregistré, mais la photo de la plaque n’a pas pu être jointe : ${messageErreur(err)}`, true); }
      }
      enregistre(r.id);
    } catch (err) {
      if (err instanceof ErreurApi && Array.isArray(err.details)) setErreurs(Object.fromEntries((err.details as any[]).map((d) => [d.champ, d.message])));
      setErreur(messageErreur(err));
    } finally { setEnvoi(false); }
  };

  const basculerProtocole = (p: string) => setF({ ...f, protocols: f.protocols.includes(p) ? f.protocols.filter((x) => x !== p) : [...f.protocols, p] });
  /** Aide d'un champ : marquage « Lu sur la photo — à confirmer » quand la valeur vient de la lecture. */
  const aide = (k: ChampLu, base?: ReactNode) => (lus[k] ? <MarqueIa texte={LU_SUR_PHOTO} /> : base);

  return (
    <Dialogue titre={initial?.id ? 'Modifier l’équipement' : 'Ajouter un équipement'} ouvert={ouvert} fermer={fermer} large
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" chargement={envoi} onClick={soumettre as any}>Enregistrer</Bouton></>}>
      <form className="pile" onSubmit={soumettre} noValidate>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <p className="petit discret">Laissez vide ce que vous ne connaissez pas : l’information restera affichée comme inconnue.</p>
        <div className="grille-form">
          <Champ libelle="Désignation" required value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} erreur={erreurs.label} />
          <ChampChoix libelle="Catégorie" value={v('category')} onChange={maj('category')} vide="Non précisée" options={CATEGORIES.map((c) => ({ valeur: c, libelle: c }))} />
        </div>
        <div className="photo-lecture">
          <BoutonPhoto icone={<ScanText aria-hidden />} surPhoto={lirePhoto} chargement={lecture} desactive={!lp.possible} explication={lp.raison}>
            Lire la plaque
          </BoutonPhoto>
          {lp.possible && (
            <span className="tres-petit discret">
              Photographiez la plaque signalétique : les champs vides sont pré-remplis, à vérifier.
              {initial?.id ? ' La photo est ajoutée à la fiche.' : ' La photo sera jointe à la fiche à l’enregistrement.'}
            </span>
          )}
        </div>
        {erreurLecture && <Alerte ton="danger" titre="Lecture de la plaque impossible">{erreurLecture}</Alerte>}
        {infos && <ResultatPlaque infos={infos} nbLus={Object.keys(lus).length} ecarts={ecarts} f={f} utiliser={utiliserLue} ajouterAuxNotes={ajouterAuxNotes} notesLues={notesLues} />}
        <div className="grille-form">
          <Champ libelle="Fabricant" list="fabricants-connus" value={v('manufacturer')} onChange={maj('manufacturer')} aide={aide('manufacturer')} />
          <Champ libelle="Gamme" value={v('range_name')} onChange={maj('range_name')} aide={aide('range_name')} />
          <Champ libelle="Référence exacte" mono value={v('reference')} onChange={maj('reference')} aide={aide('reference', 'Telle qu’inscrite sur la plaque ou l’étiquette.')} />
        </div>
        <datalist id="fabricants-connus">{fabricants.data?.map((m) => <option key={m.nom} value={m.nom} />)}</datalist>
        <div className="grille-form">
          <Champ libelle="Version matérielle" mono value={v('hw_version')} onChange={maj('hw_version')} aide={aide('hw_version')} />
          <Champ libelle="Version logicielle" mono value={v('sw_version')} onChange={maj('sw_version')} aide={aide('sw_version')} />
          <Champ libelle="Firmware" mono value={v('firmware')} onChange={maj('firmware')} aide={aide('firmware')} />
        </div>
        <fieldset className="pile" style={{ border: 0, padding: 0, margin: 0, gap: 6 }}>
          <legend className="champ-libelle" style={{ marginBottom: 6 }}>Protocoles</legend>
          <div className="rangee" style={{ gap: 6 }}>
            {PROTOCOLES.map((p) => (
              <button key={p} type="button" className={`btn btn-compact ${f.protocols.includes(p) ? 'btn-principal' : ''}`} aria-pressed={f.protocols.includes(p)} onClick={() => basculerProtocole(p)}>{p}</button>
            ))}
          </div>
        </fieldset>
        <div className="grille-form">
          <Champ libelle="Adresse réseau / bus" mono value={v('network_address')} onChange={maj('network_address')} aide="Adresse KNX, instance BACnet, n° esclave Modbus, IP… Jamais de mot de passe." />
          <Champ libelle="N° de série" mono value={v('serial_number')} onChange={maj('serial_number')} aide={aide('serial_number')} />
          <Champ libelle="Mise en service" type="date" value={v('installed_on')} onChange={maj('installed_on')} />
        </div>
        <div className="grille-form">
          <ChampChoix libelle="Emplacement" value={v('location_id')} onChange={maj('location_id')} vide="Non précisé"
            options={lieux.map((l) => ({ valeur: l.id, libelle: cheminLieu(lieux, l.id) }))} />
          <ChampChoix libelle="Système" value={v('system_id')} onChange={maj('system_id')} vide="Aucun"
            options={systemes.map((s) => ({ valeur: s.id, libelle: s.name }))} />
        </div>
        <ChampTexte libelle="Notes" value={v('notes')} onChange={maj('notes')} aide={notesLues ? <MarqueIa texte={LU_SUR_PHOTO} /> : undefined} />
        {aConfirmer && (
          <div className="photo-confirmation">
            <label className="case">
              <input ref={caseConfirmation} type="checkbox" checked={confirme} onChange={(e) => { setConfirme(e.target.checked); setErreurs((x) => ({ ...x, confirmation: '' })); }}
                aria-invalid={!!erreurs.confirmation || undefined} aria-describedby={erreurs.confirmation ? 'confirmation-lecture-erreur' : undefined} />
              <span>J’ai comparé les valeurs marquées « Lu sur la photo » avec la plaque : elles sont exactes.</span>
            </label>
            {erreurs.confirmation && <span id="confirmation-lecture-erreur" className="champ-erreur">{erreurs.confirmation}</span>}
          </div>
        )}
      </form>
    </Dialogue>
  );
}

function ResultatPlaque({ infos, nbLus, ecarts, f, utiliser, ajouterAuxNotes, notesLues }: {
  infos: Pick<Plaque, 'autres' | 'illisible'> & { vide: boolean }; nbLus: number; ecarts: { champ: ChampLu; valeur: string }[];
  f: EquipementSaisie; utiliser: (c: ChampLu, v: string) => void; ajouterAuxNotes: () => void; notesLues: boolean;
}) {
  if (infos.vide) {
    return <Alerte ton="ambre" titre="Aucune valeur lisible sur la photo">Reprenez la photo plus nette, de face et sans reflet, ou saisissez les valeurs à la main.</Alerte>;
  }
  return (
    <div className="pile photo-resultat" aria-live="polite">
      <Alerte ton="info" titre="Plaque lue par l’assistance">
        {nbLus ? `${nbLus} champ(s) vide(s) pré-rempli(s) : vérifiez chaque valeur marquée sur la plaque, corrigez si besoin, puis confirmez en bas du formulaire.`
          : 'Aucun champ vide à pré-remplir.'}
      </Alerte>
      {ecarts.length > 0 && (
        <Alerte ton="ambre" titre="Valeurs lues différentes de la saisie (non remplacées)">
          <ul className="photo-ecarts">
            {ecarts.map((x) => (
              <li key={x.champ}>
                <span>{LIBELLES_LUS[x.champ]} : saisi <span className="mono">« {(f[x.champ] as string) ?? ''} »</span>, lu <span className="mono">« {x.valeur} »</span></span>
                <Bouton onClick={() => utiliser(x.champ, x.valeur)}>Utiliser la valeur lue</Bouton>
              </li>
            ))}
          </ul>
        </Alerte>
      )}
      {infos.autres.length > 0 && (
        <div className="pile" style={{ gap: 6 }}>
          <span className="champ-libelle">Autres mentions lues</span>
          <ul className="photo-mentions petit">{infos.autres.map((a, i) => <li key={i}>{a.libelle} : <span className="mono">{a.valeur}</span></li>)}</ul>
          <MarqueIa texte={LU_SUR_PHOTO} />
          {!notesLues && <div><Bouton onClick={ajouterAuxNotes}>Ajouter aux notes</Bouton></div>}
        </div>
      )}
      {infos.illisible.length > 0 && <p className="petit discret">Illisible sur la photo : {infos.illisible.join(' ; ')}.</p>}
    </div>
  );
}
